<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>内容审核</h1>
        <p>动态图片的机器复核、以及评论 / 回复的人工审核，都在这一页</p>
      </div>
      <div class="head-actions">
        <el-button class="ghost-btn" icon="el-icon-refresh" @click="handleRefresh">刷新</el-button>
      </div>
    </div>

    <!-- ── 分栏：动态 / 评论 ─────────────────────────────── -->
    <!-- 评论审核不单独占一个菜单：新增菜单要改 MenuConstant 的静态初始化块，
         必须重启后端才生效，为一张 tab 付这个代价不值得。权限沿用 /audit/moment。 -->
    <div class="audit-tabs">
      <div
        class="audit-tab"
        :class="{ 'is-active': activeTab === 'moment' }"
        @click="activeTab = 'moment'"
      >动态审核</div>
      <div
        class="audit-tab"
        :class="{ 'is-active': activeTab === 'comment' }"
        @click="activeTab = 'comment'"
      >评论审核</div>
    </div>

    <!-- ── 指标（可点击筛选） ─────────────────────────────── -->
    <div class="stat-row" v-if="activeTab === 'moment'">
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.auditStatus === 'REVIEW' }"
        @click="quickFilter('REVIEW')"
      >
        <div class="stat-num is-warn">{{ pendingCount || 0 }}</div>
        <div class="stat-label">待人工复核</div>
        <div class="stat-sub">机器已放行的不在这里</div>
      </div>
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.auditStatus === 'ALL' }"
        @click="quickFilter('ALL')"
      >
        <div class="stat-num">{{ total }}</div>
        <div class="stat-label">当前筛选结果</div>
        <div class="stat-sub">含已通过 / 已驳回</div>
      </div>
    </div>

    <!-- ── 说明 ───────────────────────────────────────────── -->
    <div class="hint-bar audit-hint" v-if="activeTab === 'moment'">
      <i class="el-icon-info" />
      <span>
        <b>机器拿不准的、以及被判违规的，都在这里。</b>七牛鉴黄判为色情（pulp）会直接转「违规」—— 动态不公开、只在作者自己的「我发布的动态」里带违规标签；举报被判定成立也转同一个状态。
        标签为「性感」、置信度低于阈值、或审核接口异常（超时 / 图片无法解码）的转到这里等人工复核。
        通过后该动态进入心动圈，涉及的图片也一并标记为通过，同一张图不会重复进审；<b>对「违规」的动态点「恢复」即是撤销误判</b>。
      </span>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar" v-if="activeTab === 'moment'">
      <el-input
        v-model="query.keyword"
        placeholder="搜索动态内容"
        clearable
        prefix-icon="el-icon-search"
        class="search-input audit-search"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-select v-model="query.auditStatus" class="status-select" @change="handleSearch">
        <el-option label="待人工复核" value="REVIEW" />
        <el-option label="已通过" value="PASS" />
        <el-option label="违规" value="BLOCK" />
        <el-option label="全部" value="ALL" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleSearch">查询</el-button>
      <el-button class="ghost-btn" @click="resetQuery">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 条</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card" v-if="activeTab === 'moment'">
      <el-table v-loading="loading" :data="list" style="width: 100%">
        <el-table-column label="用户" min-width="190">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <img class="owner-avatar audit-avatar" :src="row.avatar || defaultAvatar" />
              <div class="owner-info">
                <div class="owner-name">{{ row.nickname || '未设置昵称' }}</div>
                <div class="owner-school">{{ row.schoolName || '—' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="动态内容" min-width="240">
          <template slot-scope="{ row }">
            <div class="cell-main content-clamp">{{ row.content || '（无文字）' }}</div>
            <div v-if="row.auditReason" class="cell-sub">{{ row.auditReason }}</div>
          </template>
        </el-table-column>

        <el-table-column label="图片" width="180">
          <template slot-scope="{ row }">
            <div class="thumb-row">
              <img
                v-for="(img, i) in thumbs(row)"
                :key="i"
                class="thumb"
                :class="{ 'is-flagged': isFlagged(img) }"
                :src="img.url"
              />
              <span v-if="row.images && row.images.length > 3" class="thumb-more">
                +{{ row.images.length - 3 }}
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="发布时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.createdAt || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="pillClass(row.auditStatus)">
              <i />{{ statusText(row.auditStatus) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="210" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click="openDetail(row)">查看</button>
              <template v-if="canReview(row)">
                <button type="button" class="act act-primary" @click="approve(row)">
                  {{ isRestoring(row) ? '恢复' : '通过' }}
                </button>
                <button
                  v-if="!isRestoring(row)"
                  type="button"
                  class="act act-danger"
                  @click="reject(row)"
                >驳回</button>
              </template>
              <span v-else class="row-done">已通过</span>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="empty-state">
            <i class="el-icon-circle-check" />
            <p>没有待复核的内容，机器判定明确的都已经自动处理完了</p>
          </div>
        </template>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :current-page.sync="query.page"
          :page-size.sync="query.size"
          :page-sizes="[10, 20, 50]"
          @current-change="fetchList"
          @size-change="handleSearch"
        />
      </div>
    </div>

    <!-- ── 评论审核面板 ───────────────────────────────────── -->
    <comment-audit-panel v-if="activeTab === 'comment'" ref="commentPanel" />

    <!-- ── 审核详情（一屏纵览） ───────────────────────────── -->
    <el-drawer
      :visible.sync="drawerVisible"
      size="620px"
      custom-class="admin-drawer"
      :with-header="false"
    >
      <div class="audit-detail">
        <div class="audit-detail__head">
          <div class="audit-detail__who">
            <img class="audit-detail__avatar" :src="current.avatar || defaultAvatar" />
            <div>
              <div class="audit-detail__name">{{ current.nickname || '未设置昵称' }}</div>
              <div class="audit-detail__meta">
                {{ current.schoolName || '—' }} · {{ current.createdAt || '—' }}
              </div>
            </div>
          </div>
          <span class="status-pill" :class="pillClass(current.auditStatus)">
            <i />{{ statusText(current.auditStatus) }}
          </span>
        </div>

        <div class="audit-detail__section">
          <div class="audit-detail__label">动态内容</div>
          <div class="audit-detail__content">{{ current.content || '（无文字）' }}</div>
        </div>

        <div class="audit-detail__section">
          <div class="audit-detail__label">
            图片
            <span class="audit-detail__count">{{ (current.images || []).length }} 张</span>
          </div>
          <div v-if="!(current.images || []).length" class="audit-detail__empty">这条动态没有图片</div>
          <div v-else class="audit-gallery">
            <div v-for="(img, i) in current.images" :key="i" class="audit-gallery__item">
              <el-image
                class="audit-gallery__img"
                :src="img.url"
                :preview-src-list="previewList"
                :initial-index="i"
                fit="cover"
              />
              <div class="audit-gallery__meta">
                <span class="label-chip" :class="labelClass(img)">
                  {{ labelText(img.auditLabel) }}
                </span>
                <span class="audit-gallery__score">{{ scoreText(img.auditScore) }}</span>
              </div>
              <div v-if="img.auditReason" class="audit-gallery__reason">{{ img.auditReason }}</div>
            </div>
          </div>
        </div>

        <div v-if="current.auditReason || current.auditRemark" class="audit-detail__section">
          <div class="audit-detail__label">审核说明</div>
          <div class="audit-detail__reason">
            <div v-if="current.auditReason">系统：{{ current.auditReason }}</div>
            <div v-if="current.auditRemark">人工：{{ current.auditRemark }}</div>
          </div>
        </div>

        <div v-if="canReview(current)" class="audit-detail__footer">
          <el-button class="ghost-btn" @click="drawerVisible = false">关闭</el-button>
          <el-button v-if="!isRestoring(current)" type="danger" class="danger-btn" @click="reject(current)">驳回</el-button>
          <el-button type="primary" class="primary-btn" @click="approve(current)">
            {{ isRestoring(current) ? '恢复展示' : '通过并发布' }}
          </el-button>
        </div>
        <div v-else class="audit-detail__footer">
          <el-button class="ghost-btn" @click="drawerVisible = false">关闭</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getContentAuditList, approveContentAudit, rejectContentAudit } from '@/api/audit'
import CommentAuditPanel from './components/CommentAuditPanel.vue'

export default {
  name: 'ContentAudit',
  components: { CommentAuditPanel },
  data() {
    return {
      /** moment = 动态（机器复核）；comment = 评论 / 回复（人工先审后发） */
      activeTab: 'moment',
      loading: false,
      list: [],
      total: 0,
      pendingCount: 0,
      /** 深链定位用：由后端在 focus 参数下单独返回，不在当前页也拿得到 */
      focusItem: null,
      query: {
        page: 1,
        size: 10,
        auditStatus: 'REVIEW',
        keyword: ''
      },
      drawerVisible: false,
      current: {},
      defaultAvatar: 'https://xdmh.xbjava.com/static/default-avatar.png'
    }
  },
  computed: {
    previewList() {
      return (this.current.images || []).map(img => img.url).filter(Boolean)
    }
  },
  created() {
    // 深链：钉钉消息带 ?focus=<动态ID>。待审列表按创建时间升序，刚来的那条
    // 永远在最后一页，靠 focusItem 由后端单独捞出来（见 AdminContentAuditController#list）
    this.fetchList().then(() => this.applyFocus())
  },
  methods: {
    /** 刷新按钮：刷新当前这一栏，不要去重置另一栏的筛选 */
    handleRefresh() {
      if (this.activeTab === 'comment') {
        const panel = this.$refs.commentPanel
        if (panel) panel.fetchList()
        return
      }
      this.fetchList()
    },
    fetchList() {
      this.loading = true
      getContentAuditList({
        page: this.query.page,
        size: this.query.size,
        auditStatus: this.query.auditStatus,
        keyword: this.query.keyword
      })
        .then(res => {
          const data = res.data || {}
          this.list = data.list || []
          this.total = data.total || 0
          this.pendingCount = data.pendingCount || 0
          this.focusItem = data.focusItem || null
        })
        .finally(() => {
          this.loading = false
        })
    },
    applyFocus() {
      const id = Number(this.$route.query.focus)
      if (id && this.focusItem && this.focusItem.id === id) {
        this.openDetail(this.focusItem)
      }
      if (this.$route.query.focus) {
        const query = { ...this.$route.query }
        delete query.focus
        this.$router.replace({ path: this.$route.path, query }).catch(() => {})
      }
    },
    handleSearch() {
      this.query.page = 1
      this.fetchList()
    },
    resetQuery() {
      this.query = { page: 1, size: 10, auditStatus: 'REVIEW', keyword: '' }
      this.fetchList()
    },
    quickFilter(status) {
      this.query.auditStatus = status
      this.handleSearch()
    },
    openDetail(row) {
      this.current = row
      this.drawerVisible = true
    },
    canReview(row) {
      return row && (row.auditStatus === 'REVIEW' || row.auditStatus === 'PENDING' || row.auditStatus === 'BLOCK')
    },
    /** 违规的动态点「通过」等于恢复展示 —— 误杀与误报的唯一出口 */
    isRestoring(row) {
      return row && row.auditStatus === 'BLOCK'
    },
    approve(row) {
      if (!row || !row.id) return
      const restoring = this.isRestoring(row)
      this.$confirm(
        restoring
          ? '恢复后这条动态会重新出现在心动圈。确认原判定有误？'
          : '通过后这条动态会立即出现在心动圈，涉及的图片也会一并标记为通过。确认内容没有问题？',
        restoring ? '恢复展示' : '通过审核',
        { confirmButtonText: restoring ? '确认恢复' : '确认通过', cancelButtonText: '取消', type: 'warning' }
      )
        .then(() => {
          return approveContentAudit(row.id, {})
        })
        .then(res => {
          this.$message.success((res && res.message) || (restoring ? '已恢复展示' : '已通过，动态已发布'))
          this.drawerVisible = false
          this.fetchList()
        })
        .catch(() => {})
    },
    reject(row) {
      if (!row || !row.id) return
      this.$prompt('驳回原因会推送给发布者，请写清楚哪里不合适', '驳回动态', {
        confirmButtonText: '确认驳回',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：图片含有明显性暗示，请更换后重新发布',
        inputValidator: value => {
          if (!value || !value.trim()) return '请填写驳回原因'
          if (value.length > 100) return '原因请控制在 100 字以内'
          return true
        }
      })
        .then(({ value }) => {
          return rejectContentAudit(row.id, { remark: value.trim() })
        })
        .then(() => {
          this.$message.success('已驳回')
          this.drawerVisible = false
          this.fetchList()
        })
        .catch(() => {})
    },
    thumbs(row) {
      const images = row && row.images ? row.images : []
      return images.slice(0, 3)
    },
    isFlagged(img) {
      return img && (img.auditStatus === 'REVIEW' || img.auditStatus === 'BLOCK')
    },
    pillClass(status) {
      if (status === 'PASS') return 's1'
      if (status === 'BLOCK') return 's2'
      return 's0'
    },
    statusText(status) {
      if (status === 'PASS') return '已通过'
      if (status === 'BLOCK') return '违规'
      if (status === 'PENDING') return '待处理'
      return '待复核'
    },
    labelClass(img) {
      const label = img && img.auditLabel
      if (label === 'pulp') return 'is-pulp'
      if (label === 'sexy') return 'is-sexy'
      return 'is-normal'
    },
    labelText(label) {
      if (label === 'pulp') return '色情'
      if (label === 'sexy') return '性感'
      if (label === 'normal') return '正常'
      return '未识别'
    },
    scoreText(score) {
      if (score === null || score === undefined || score === '') return '—'
      const num = Number(score)
      if (isNaN(num)) return '—'
      return '置信度 ' + Math.round(num * 100) + '%'
    }
  }
}
</script>

<style lang="scss" scoped>
/* 分栏
   ------------------------------------------------------------
   两栏是同一件事的两个面（机器复核 / 人工审核），所以用下划线式分栏
   而不是并排按钮 —— 按钮会读成"两个动作"，下划线读成"两个视图"。 */
.audit-tabs {
  display: flex;
  align-items: center;
  gap: 28px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--ink-100);
}

.audit-tab {
  position: relative;
  padding: 0 2px 12px;
  font-size: 14px;
  color: var(--ink-500);
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}

.audit-tab:hover {
  color: var(--ink-700);
}

.audit-tab.is-active {
  color: var(--rose-600);
  font-weight: 500;
}

.audit-tab.is-active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  border-radius: 2px;
  background: var(--rose-500);
}

.stat-card.is-clickable {
  cursor: pointer;
  user-select: none;
}

.stat-card.is-clickable:hover {
  border-color: var(--ink-300);
}

.stat-card.is-clickable.is-active {
  border-color: var(--rose-400);
  box-shadow: 0 0 0 3px var(--rose-50);
}

.stat-num.is-warn {
  color: var(--warn);
}

.audit-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  line-height: 1.7;
}

.audit-hint i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.audit-search {
  width: 240px;
}

.status-select {
  width: 150px;
}

.audit-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.content-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.row-done {
  font-size: 12.5px;
  color: var(--ink-400);
}

.danger-btn {
  color: #fff;
  background: var(--rose-600);
  border-color: var(--rose-600);
}

.danger-btn:hover,
.danger-btn:focus {
  background: var(--rose-500);
  border-color: var(--rose-500);
  color: #fff;
}

/* 列表缩略图 */
.thumb-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.thumb {
  width: 40px;
  height: 40px;
  border-radius: var(--r-sm);
  object-fit: cover;
  background: var(--ink-100);
  border: 1px solid transparent;
}

.thumb.is-flagged {
  border-color: var(--rose-400);
}

.thumb-more {
  font-size: 12px;
  color: var(--ink-400);
}

/* 抽屉 */
.audit-detail {
  padding: 24px 28px 28px;
}

.audit-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--ink-100);
}

.audit-detail__who {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.audit-detail__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}

.audit-detail__name {
  font-size: 15px;
  font-weight: 500;
  color: var(--ink-900);
}

.audit-detail__meta {
  font-size: 12.5px;
  color: var(--ink-400);
  margin-top: 2px;
}

.audit-detail__section {
  margin-top: 22px;
}

.audit-detail__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-500);
  margin-bottom: 10px;
}

.audit-detail__count {
  font-weight: 400;
  color: var(--ink-400);
  margin-left: 6px;
}

.audit-detail__content {
  font-size: 14px;
  line-height: 1.8;
  color: var(--ink-900);
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--ink-100);
  border-radius: var(--r-md);
  padding: 12px 14px;
}

.audit-detail__empty {
  font-size: 13px;
  color: var(--ink-400);
}

.audit-detail__reason {
  font-size: 13px;
  line-height: 1.9;
  color: var(--ink-500);
}

.audit-gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.audit-gallery__item {
  min-width: 0;
}

.audit-gallery__img {
  width: 100%;
  height: 132px;
  border-radius: var(--r-md);
  background: var(--ink-100);
  display: block;
}

.audit-gallery__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 6px;
}

.audit-gallery__score {
  font-size: 11.5px;
  color: var(--ink-400);
}

.audit-gallery__reason {
  font-size: 11.5px;
  color: var(--ink-400);
  margin-top: 3px;
  line-height: 1.5;
}

.label-chip {
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 999px;
  line-height: 1.6;
  white-space: nowrap;
}

.label-chip.is-normal {
  background: var(--ok-bg);
  color: var(--ok-text);
}

.label-chip.is-sexy {
  background: #fdf4e3;
  color: var(--warn-text);
}

.label-chip.is-pulp {
  background: var(--rose-50);
  color: var(--rose-600);
}

.audit-detail__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid var(--ink-100);
}
</style>
